import {
  CalendarTimelineDefaultIcon,
  DeleteIcon,
  EditIcon,
} from '@amzn/ring-safe-web';
import {
  Check,
  Delete,
  InfoOutline,
  Report,
  Reset,
  ViewOff,
} from '@amzn/ring-ui-react-components';
import { equals } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsNpssUser } from 'src/auth';
import { useFeature } from 'src/context';
import { DropdownItem } from 'src/shared/components/Dropdown';
import { useIsSameAgency } from 'src/shared/hooks';
import { trackAnalyticsEvent } from 'src/utils/analytics';
import type {
  Event,
  EventDetails,
  EventResolutionInformation,
  EventUser,
} from '@amzn/ring-neighbors-api-orchestrator-gql-schema';

export interface UseEventDropdownOptionsProps
  extends Pick<Event, 'is_owned'>,
    Pick<EventDetails, 'category'>,
    Pick<EventUser, 'agency_id'>,
    Partial<Pick<EventResolutionInformation, 'is_resolved'>> {
  onClickCategoryDefinitions: () => void;
  onClickHide: () => void;
  onClickDelete?: () => void;
  onClickReport: () => void;
  onClickResolve: () => void;
  onClickReSchedule?: () => void;
  onClickEditScheduledPost?: () => void;
}

// TODO: deprecate this hook
export const useEventDropdownOptions = ({
  is_owned,
  is_resolved,
  category,
  agency_id,
  onClickCategoryDefinitions,
  onClickHide,
  onClickDelete,
  onClickReport,
  onClickResolve,
  onClickReSchedule,
  onClickEditScheduledPost,
}: UseEventDropdownOptionsProps) => {
  const { t } = useTranslation('components.event', { useSuspense: false });
  const enableResolveSwitch = useFeature('ENABLE_RESOLVE_SWITCH');
  const isNpssScheduledPost = equalsNpssScheduledPostCategory(category);
  const isNpssUser = useIsNpssUser();
  const isSameAgency = useIsSameAgency(agency_id);

  const options = useMemo(() => {
    const handleHide = () => {
      onClickHide?.();

      trackAnalyticsEvent('Hide Post Click');
    };

    const handleReport = () => {
      onClickReport?.();

      trackAnalyticsEvent('Report Post Click');
    };

    const npssUserCanResolvePost = isNpssUser && isSameAgency;
    const npssUserCanDeletePost = isNpssUser && isSameAgency && is_owned;
    const nhwebUserCanEdit = !isNpssUser && is_owned;
    const isPublicAssistance = equalsPublicAssistanceCategory(category);

    const CategoryDefinitionsOptions = [
      <DropdownItem
        icon={<InfoOutline mr={2} color="neutral.500" boxSize={8} />}
        key="categoryDefinitions"
        onClick={onClickCategoryDefinitions}
        data-testid="category-definitions"
      >
        {t('dropdown.category-definitions')}
      </DropdownItem>,
    ];

    const ResolvePostDropdownOptions = [
      !enableResolveSwitch ||
      (isNpssUser && !npssUserCanResolvePost) ||
      (!isNpssUser && !nhwebUserCanEdit) ? (
        <></>
      ) : is_resolved ? (
        <DropdownItem
          icon={<Reset mr={2} color="neutral.500" boxSize={8} />}
          key="unresolve"
          onClick={onClickResolve}
          data-testid="unresolve-post"
        >
          {t('dropdown.unresolve')}
        </DropdownItem>
      ) : (
        <DropdownItem
          icon={<Check mr={2} color="neutral.500" boxSize={8} />}
          key="resolve"
          onClick={onClickResolve}
          data-testid="resolve-post"
        >
          {t('dropdown.resolve')}
        </DropdownItem>
      ),
    ];

    const DeletePostDropdownOptions = [
      npssUserCanDeletePost || nhwebUserCanEdit ? (
        <DropdownItem
          icon={
            <Delete mr={2} boxSize={8} color="negative.500" name="delete" />
          }
          destructive={true}
          key="delete"
          onClick={onClickDelete}
          data-testid="delete-post"
        >
          {t('dropdown.delete')}
        </DropdownItem>
      ) : (
        <></>
      ),
    ];

    const HidePostOptions = [
      <DropdownItem
        icon={<ViewOff mr={2} boxSize={8} color="negative.500" />}
        key="hide"
        destructive={true}
        onClick={handleHide}
        data-testid="hide-post"
      >
        {t('dropdown.hide')}
      </DropdownItem>,
    ];

    const ReportPostOptions = !isPublicAssistance
      ? [
          <DropdownItem
            icon={<Report mr={2} color="neutral.500" boxSize={8} />}
            key="report"
            onClick={handleReport}
            data-testid="report-post"
          >
            {t('dropdown.report')}
          </DropdownItem>,
        ]
      : [];

    const schedulePostDropdownOptions = [
      <DropdownItem
        icon={
          <CalendarTimelineDefaultIcon
            width={32}
            style={{ marginRight: 'var(--ring-space-2)' }}
          />
        }
        key="re-schedule"
        onClick={onClickReSchedule}
        data-testid="reschedule-post"
      >
        {t('dropdown.reschedule')}
      </DropdownItem>,
      <DropdownItem
        icon={
          <EditIcon width={32} style={{ marginRight: 'var(--ring-space-2)' }} />
        }
        onClick={onClickEditScheduledPost}
        key="edit-scheduled-post"
        data-testid="edit-scheduled-post"
      >
        {t('dropdown.edit-description')}
      </DropdownItem>,
      <DropdownItem
        icon={
          <DeleteIcon
            width={32}
            fill="var(--negative-600)"
            style={{ marginRight: 'var(--ring-space-2)' }}
          />
        }
        destructive={true}
        key="delete-scheduled"
        onClick={onClickDelete}
        data-testid="delete-scheduled-post"
      >
        {t('dropdown.delete')}
      </DropdownItem>,
    ];

    // Dropdown options for Neighbors users
    if (!isNpssUser) {
      const baseOptions = [
        ...CategoryDefinitionsOptions,
        ...ReportPostOptions,
        ...HidePostOptions,
      ];
      if (nhwebUserCanEdit) {
        baseOptions.push(
          ...ResolvePostDropdownOptions,
          ...DeletePostDropdownOptions,
        );
      }
      return baseOptions;
    }

    // Dropdown options for NPSS users
    if (isNpssUser && !isSameAgency && !isNpssScheduledPost) return undefined;

    if (isNpssUser) {
      return isNpssScheduledPost
        ? [...schedulePostDropdownOptions]
        : [...ResolvePostDropdownOptions, ...DeletePostDropdownOptions];
    }
  }, [
    category,
    is_owned,
    is_resolved,
    onClickCategoryDefinitions,
    onClickDelete,
    onClickHide,
    onClickReport,
    onClickResolve,
    enableResolveSwitch,
    t,
    isNpssScheduledPost,
    onClickReSchedule,
    onClickEditScheduledPost,
    isSameAgency,
    isNpssUser,
  ]);

  return options;
};

const equalsPublicAssistanceCategory = equals('publicassistance');

const equalsNpssScheduledPostCategory = equals('scheduled');
